// @ts-nocheck
import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { flushSync } from 'react-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Image } from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import FontSize from '../../../SolyticsEditor/extensions/font-size/fontSize';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  ButtonBase,
} from '@mui/material';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { ReactComponent as Close } from '../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as InsertDriveFileIcon } from '../../../../assets/Icons/lucideIcons/file.svg';
import { ReactComponent as CancelIcon } from '../../../../assets/Icons/lucideIcons/circle-x.svg';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import {
  EMPTY_ARRAY,
  MENTIONED_ENTITIES,
  NOOP,
} from '../../../../const/CommonConst';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';
import { useFetchSharableLinkQuery } from '../../../../features/configuration/documentTemplate/api/documentTemplateQueries';
import CustomPopover from '../../CustomPopover';
import EditorToolBar from './EditorToolbar';
import { MentionContext } from './MentionContext';
import {
  createUserMentionExtension,
  createModelMentionExtension,
  createArtifactMentionExtension,
  createDocumentMentionExtension,
  createAttributeMentionExtension,
} from './extensions/createMentionExtensions';
import { draftRawToTipTapHtml } from './utils/draftTipTapConverter';

/** Returns true if value looks like legacy DraftJS raw content */
function isDraftContent(content) {
  if (!content) return false;
  if (typeof content === 'object' && Array.isArray(content.blocks)) return true;
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      return typeof parsed === 'object' && Array.isArray(parsed?.blocks);
    } catch {
      return false;
    }
  }
  return false;
}

/** Returns true if value looks like TipTap native JSON ({ type: 'doc' }) */
function isTipTapContent(content) {
  if (!content) return false;
  if (typeof content === 'object' && content.type === 'doc') return true;
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      return parsed?.type === 'doc';
    } catch {
      return false;
    }
  }
  return false;
}

/** Map MENTIONED_ENTITIES mentionType to TipTap node name */
const MENTION_TYPE_TO_NODE = {
  [MENTIONED_ENTITIES.user.mentionType]: 'userMention',
  [MENTIONED_ENTITIES.model.mentionType]: 'modelMention',
  [MENTIONED_ENTITIES.artifact.mentionType]: 'artifactMention',
  [MENTIONED_ENTITIES.document.mentionType]: 'documentMention',
  [MENTIONED_ENTITIES.attribute.mentionType]: 'attributeMention',
};

const EDITOR_CONTENT_UPDATE_DEBOUNCE_MS = 250;

function stringifyEditorContent(content) {
  if (!content) return '';
  if (typeof content === 'string') return content;
  try {
    return JSON.stringify(content);
  } catch {
    return '';
  }
}

function hasTipTapText(node) {
  if (!node) return false;
  if (node.type === 'text') return Boolean(node.text?.trim());
  return (node.content || []).some(hasTipTapText);
}

const FilePreview = ({ file, handleRemoveFile }) => {
  const fileName = file?.name;
  const handleClick = useCallback(
    () => handleRemoveFile(file),
    [handleRemoveFile, file]
  );
  return (
    <Box
      sx={{
        display: 'flex',
        border: (theme) => `1px solid ${theme.palette.other.attachmentBorder}`,
        width: '200px',
        borderRadius: '4px',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 60,
          width: 60,
        }}
      >
        <InsertDriveFileIcon sx={{ height: '100%', width: '100%' }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="body1"
          component="div"
          title={fileName}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.primary',
            wordBreak: 'break-word',
            whiteSpace: 'nowrap',
            maxWidth: '140px',
          }}
        >
          {fileName}
        </Typography>
      </Box>
      <Box sx={{ position: 'absolute', top: -18, right: -23 }}>
        <IconButton
          color="primary"
          aria-label="remove document"
          onClick={handleClick}
        >
          <CancelIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

FilePreview.propTypes = {
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
};

const TextEditor = ({
  content = '',
  onChange = NOOP,
  userList = EMPTY_ARRAY,
  threadForm = null,
  transitionStates = EMPTY_ARRAY,
  readOnly = false,
  disableTagAndTitleFields = false,
  entityId,
  isEditorEmpty,
  handleSubmit,
  mode = 'default',
  cancelButton = null,
  submitButton = null,
  isUserAuthorizedForAddNote,
  onClose = null,
  compact = false,
  files: parentFiles,
  onAttachFile,
  onRemoveFile,
  showAttachment = false,
  hideInsertImageButton = false,
  placeholder = 'Write your message here *',
  selectedEntityToInsert = null,
  collapsible = false,
  maxLines = 1,
}) => {
  const [internalFiles, setInternalFiles] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(readOnly && collapsible);
  const [shouldShowCollapseToggle, setShouldShowCollapseToggle] =
    useState(false);
  const files = parentFiles || internalFiles;

  const editorScrollRef = useRef(null);
  // Tracks the last DraftJS JSON string emitted via onChange to prevent sync loops
  const lastEmittedRef = useRef(null);
  const latestContentRef = useRef(stringifyEditorContent(content));
  const lastInsertedEntityRef = useRef(null);
  const hasPendingContentChangeRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const handleSubmitRef = useRef(handleSubmit);
  onChangeRef.current = onChange;
  handleSubmitRef.current = handleSubmit;
  const [localIsEditorEmpty, setLocalIsEditorEmpty] = useState(isEditorEmpty);

  const { searchMentionEntities, fetchDocumentDetails, getAttributeDetails } =
    useModelInventory();
  const { execute: fetchSharableLink } = useFetchSharableLinkQuery({
    options: {
      enabled: false,
    },
  });

  const mentionContextValue = useMemo(
    () => ({ fetchDocumentDetails, fetchSharableLink, getAttributeDetails }),
    [fetchDocumentDetails, fetchSharableLink, getAttributeDetails]
  );

  // Determine initial content for TipTap (only computed on mount).
  // TipTap JSON is passed as-is; legacy DraftJS is converted to HTML; plain HTML stays.
  const initialContent = useMemo(() => {
    if (!content) return '';
    if (isTipTapContent(content)) {
      return typeof content === 'string' ? JSON.parse(content) : content;
    }
    if (isDraftContent(content)) return draftRawToTipTapHtml(content);
    return content;
  }, []); // intentionally only on mount

  const extensions = useMemo(
    () => [
      StarterKit,
      TextStyle,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Image.configure({ allowBase64: true }),
      Placeholder.configure({ placeholder }),
      FontSize,
      createUserMentionExtension({ userList }),
      createModelMentionExtension({
        searchMentionEntities,
        entityId,
      }),
      createArtifactMentionExtension({
        searchMentionEntities,
        entityId,
      }),
      createDocumentMentionExtension({
        searchMentionEntities,
        entityId,
      }),
      createAttributeMentionExtension({
        searchMentionEntities,
        entityId,
      }),
    ],
    // Extensions recreated only when entity context changes
    [entityId]
  );

  const emitContentChange = useCallback(
    (nextContent, { sync = false } = {}) => {
      lastEmittedRef.current = nextContent;
      hasPendingContentChangeRef.current = false;
      const notifyParent = () => onChangeRef.current(nextContent);
      if (sync) {
        flushSync(notifyParent);
        return;
      }
      notifyParent();
    },
    []
  );

  const debouncedEmitContentChange = useMemo(
    () =>
      debounce((nextContent) => {
        emitContentChange(nextContent);
      }, EDITOR_CONTENT_UPDATE_DEBOUNCE_MS),
    [emitContentChange]
  );

  const serializeEditorContent = useCallback((editorInstance) => {
    if (!editorInstance) return latestContentRef.current || '';
    const nextJson = editorInstance.getJSON();
    const nextContent = JSON.stringify(nextJson);
    latestContentRef.current = nextContent;
    setLocalIsEditorEmpty((prev) => {
      const nextIsEmpty = !hasTipTapText(nextJson);
      return prev === nextIsEmpty ? prev : nextIsEmpty;
    });
    return nextContent;
  }, []);

  const flushPendingContentChange = useCallback(
    (editorInstance, { sync = false } = {}) => {
      if (!hasPendingContentChangeRef.current) {
        debouncedEmitContentChange.cancel();
        return latestContentRef.current || '';
      }
      const nextContent =
        latestContentRef.current || serializeEditorContent(editorInstance);
      debouncedEmitContentChange.cancel();
      emitContentChange(nextContent, { sync });
      return nextContent;
    },
    [debouncedEmitContentChange, emitContentChange, serializeEditorContent]
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor: e }) => {
      const json = serializeEditorContent(e);
      lastEmittedRef.current = json;
      hasPendingContentChangeRef.current = true;
      debouncedEmitContentChange(json);
    },
    onBlur: ({ editor: e }) => {
      flushPendingContentChange(e, { sync: true });
    },
    editorProps: {
      handlePaste: (_view, event) => {
        // eslint-disable-next-line no-shadow
        const files = Array.from(event.clipboardData?.files || []);
        if (
          hideInsertImageButton &&
          files.some((file) => file?.type?.startsWith('image/'))
        ) {
          event.preventDefault();
          return true;
        }
        if (!files.length || !files[0].type.startsWith('image/')) return false;
        const file = files[0];
        const reader = new FileReader();
        reader.onload = () => {
          const img = new window.Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxSize = 1200;
            let { width, height } = img;
            if (width > height && width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            } else if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            const resizedBase64 = canvas.toDataURL('image/jpeg', 0.9);
            editor?.chain().focus().setImage({ src: resizedBase64 }).run();
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
        return true;
      },
    },
  });

  useEffect(
    () => () => {
      debouncedEmitContentChange.cancel();
    },
    [debouncedEmitContentChange]
  );

  useEffect(() => {
    setLocalIsEditorEmpty(isEditorEmpty);
  }, [isEditorEmpty]);

  // Keep the existing TipTap instance in sync when Chat Forum toggles read/edit.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
    if (!readOnly) {
      setTimeout(() => editor.commands.focus(), 0);
    }
  }, [editor, readOnly]);

  // Auto-scroll to bottom on content change
  useEffect(() => {
    if (editorScrollRef?.current) {
      editorScrollRef.current.scrollTop = editorScrollRef.current.scrollHeight;
    }
  }, [content]);

  // Sync external content changes (e.g. parent resets editor after submit)
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    // Skip content we just emitted — avoids the onChange→setContent loop
    if (content === lastEmittedRef.current) return;
    debouncedEmitContentChange.cancel();
    hasPendingContentChangeRef.current = false;

    let incoming;
    if (isTipTapContent(content)) {
      incoming = typeof content === 'string' ? JSON.parse(content) : content;
    } else if (isDraftContent(content)) {
      incoming = draftRawToTipTapHtml(content);
    } else {
      incoming = content || '';
    }

    const currentJson = JSON.stringify(editor.getJSON());
    const incomingStr =
      typeof incoming === 'object' ? JSON.stringify(incoming) : incoming;
    latestContentRef.current = stringifyEditorContent(incoming);

    if (currentJson !== incomingStr) {
      editor.commands.setContent(incoming, false);
    }
  }, [content, debouncedEmitContentChange, editor]);

  // Programmatic mention insertion (for AddNoteModal / pre-filled mentions)
  useEffect(() => {
    if (!selectedEntityToInsert) {
      lastInsertedEntityRef.current = null;
      return;
    }
    if (!editor) return;
    const insertionKey = stringifyEditorContent(selectedEntityToInsert);
    if (lastInsertedEntityRef.current === insertionKey) return;
    const nodeType = MENTION_TYPE_TO_NODE[selectedEntityToInsert.mentionType];
    if (!nodeType) return;

    let attrs = {};
    if (selectedEntityToInsert.user) {
      attrs = {
        id: selectedEntityToInsert.id || selectedEntityToInsert.user,
        label: selectedEntityToInsert.name || selectedEntityToInsert.user,
        name: selectedEntityToInsert.name || selectedEntityToInsert.user,
        designation: selectedEntityToInsert.designation || '',
        role: selectedEntityToInsert.role || '',
        user: selectedEntityToInsert.user || '',
        user_email: selectedEntityToInsert.user_email || '',
      };
    } else {
      attrs = {
        id: selectedEntityToInsert.id,
        label:
          `${selectedEntityToInsert.uniqueId || ''} ${selectedEntityToInsert.name || ''}`.trim(),
        uniqueId: selectedEntityToInsert.uniqueId,
        name: selectedEntityToInsert.name,
        parentEntityId: selectedEntityToInsert.entityId,
        entityId: selectedEntityToInsert.id,
        requestType: selectedEntityToInsert.requestType || '',
      };
    }

    editor
      .chain()
      .focus()
      .insertContent({ type: nodeType, attrs })
      .insertContent(' ')
      .run();
    lastInsertedEntityRef.current = insertionKey;
  }, [editor, selectedEntityToInsert]);

  const handleAttachFile = useCallback(
    (file) => {
      if (onAttachFile) {
        onAttachFile(file);
      } else {
        setInternalFiles((prev) => [...prev, ...file]);
      }
    },
    [onAttachFile]
  );

  const handleRemoveFile = useCallback(
    (file) => {
      if (onRemoveFile) {
        onRemoveFile(file);
      } else {
        setInternalFiles((prev) => prev?.filter((f) => f?.name !== file?.name));
      }
    },
    [onRemoveFile]
  );

  const handleToolbarSubmit = useCallback(
    (event) => {
      flushPendingContentChange(editor, { sync: true });
      handleSubmitRef.current?.(event);
    },
    [editor, flushPendingContentChange]
  );

  const handleSubmitButtonMouseDownCapture = useCallback(() => {
    flushPendingContentChange(editor, { sync: true });
  }, [editor, flushPendingContentChange]);

  const submitButtonWithFlush = useMemo(() => {
    if (!submitButton) return submitButton;
    return (
      <Box
        component="span"
        onMouseDownCapture={handleSubmitButtonMouseDownCapture}
      >
        {submitButton}
      </Box>
    );
  }, [handleSubmitButtonMouseDownCapture, submitButton]);

  const handleGetOptionLabel = useCallback((option) => option, []);
  const handleOptionEqualToValue = useCallback(
    (option, value) => option === value,
    []
  );

  const handleChangeTag = useCallback(
    (_event, newValue) => {
      threadForm.setFieldValue('tag', newValue);
    },
    [threadForm]
  );

  const handleOnBlurTag = useCallback(
    (e) => {
      threadForm.setFieldValue('tag', e.target.value);
    },
    [threadForm]
  );

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Workflow state or custom tag"
        placeholder="Select state or add custom tag."
        error={Boolean(threadForm?.errors?.tag)}
        helperText={threadForm?.errors?.tag}
        sx={{ wordBreak: 'break-word', whiteSpace: 'wrap' }}
        spellCheck={true}
        slotProps={{
          ...params?.slotProps,
          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [threadForm]
  );

  const renderTitleAndTags = () => {
    const shouldRender = !disableTagAndTitleFields && threadForm && !readOnly;
    if (!shouldRender) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Grid
          container
          spacing="8px"
          sx={{
            margin: '8px',
            width: '100%',
          }}
          size={12}
        >
          <Grid size={8}>
            <TextField
              required
              name="title"
              value={threadForm?.values?.title}
              {...threadForm.getFieldProps('title')}
              placeholder="Enter thread title"
              label="Thread title"
              helperText={
                threadForm?.errors?.title && threadForm?.touched?.title
                  ? threadForm.errors.title
                  : null
              }
              error={
                Boolean(threadForm?.errors?.title) && threadForm?.touched?.title
              }
              spellCheck={true}
            />
          </Grid>
          <Grid size={4}>
            <Autocomplete
              id="select_tag"
              options={transitionStates}
              disableClearable={['', undefined].includes(
                threadForm?.values?.tag
              )}
              value={threadForm?.values?.tag}
              freeSolo
              forcePopupIcon={true}
              getOptionLabel={handleGetOptionLabel}
              isOptionEqualToValue={handleOptionEqualToValue}
              onChange={handleChangeTag}
              onBlur={handleOnBlurTag}
              popupIcon={<DropdownArrow />}
              renderInput={renderInput}
              slots={{
                popper: CustomPopover,
              }}
            />
          </Grid>
        </Grid>
        {onClose && (
          <IconButton
            onClick={onClose}
            title="Close"
            variant="outlined"
            aria-label="Close editor"
            sx={{ position: 'absolute', right: '16px', top: '14px' }}
          >
            <Close />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderFilesView = () =>
    files?.length > 0 ? (
      <Stack
        direction="row"
        sx={{
          gap: 2,
          p: 2,
        }}
      >
        {files.map((file) => (
          <FilePreview
            key={file?.name}
            file={file}
            handleRemoveFile={handleRemoveFile}
          />
        ))}
      </Stack>
    ) : null;

  const handleEditorRootClick = useCallback(() => {
    if (!readOnly) {
      editor?.commands.focus();
    }
  }, [editor, readOnly]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, [setIsCollapsed]);

  const isReadOnlyCollapsible = readOnly && collapsible;
  const editableMinHeight = compact ? '60px' : '20vh';
  const editablePadding = compact ? '12px 16px' : '25px';
  const editorPadding = readOnly ? 0 : editablePadding;
  const editorMinHeight = readOnly ? 0 : editableMinHeight;
  const editorRootMinHeight = readOnly ? '0vh' : editableMinHeight;

  const editorMaxHeight = useMemo(() => {
    if (isReadOnlyCollapsible && isCollapsed) {
      return 'none';
    }
    if (compact) {
      return '20vh';
    }
    return '40vh';
  }, [compact, isCollapsed, isReadOnlyCollapsible]);

  useEffect(() => {
    if (!isReadOnlyCollapsible || !editorScrollRef.current) {
      setShouldShowCollapseToggle(false);
      return undefined;
    }

    const proseMirror = editorScrollRef.current.querySelector('.ProseMirror');
    if (!proseMirror) {
      setShouldShowCollapseToggle(false);
      return undefined;
    }

    const checkOverflow = () => {
      const hasOverflow =
        proseMirror.scrollHeight > proseMirror.clientHeight + 1;
      setShouldShowCollapseToggle(isCollapsed ? hasOverflow : true);
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(checkOverflow)
        : null;
    resizeObserver?.observe(proseMirror);

    return () => {
      resizeObserver?.disconnect();
    };
  }, [content, editor, isCollapsed, isReadOnlyCollapsible, maxLines]);

  return (
    <MentionContext.Provider value={mentionContextValue}>
      <Box
        className={`RichEditorOuterBox ${readOnly ? 'read-only' : 'edit-only'}`}
      >
        {renderTitleAndTags()}
        <Box
          onClick={handleEditorRootClick}
          ref={editorScrollRef}
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            '& .ProseMirror': {
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              padding: editorPadding,
              outline: 'none',
              minHeight: editorMinHeight,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              ...(isReadOnlyCollapsible && isCollapsed
                ? {
                    display: '-webkit-box',
                    WebkitLineClamp: maxLines,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.5,
                  }
                : {}),
            },
            '& .ProseMirror ol': {
              listStyleType: 'decimal',
              paddingLeft: '1.5em',
            },
            '& .ProseMirror ul': { paddingLeft: '1.5em' },
            '& .ProseMirror p': {
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              maxWidth: '100%',
            },
            '& .ProseMirror p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              color: (theme) => theme.palette.text.disabled,
              pointerEvents: 'none',
              float: 'left',
              height: 0,
            },
            minHeight: editorRootMinHeight,
            maxHeight: editorMaxHeight,
            overflow: isReadOnlyCollapsible && isCollapsed ? 'hidden' : 'auto',
          }}
          className="RichEditor-root"
        >
          <EditorContent
            editor={editor}
            aria-label="Message editor"
            spellCheck={true}
          />
        </Box>
        {!readOnly && (
          <EditorToolBar
            editor={editor}
            handleAttachFile={handleAttachFile}
            isEditorEmpty={localIsEditorEmpty}
            handleSubmit={handleSubmit ? handleToolbarSubmit : handleSubmit}
            mode={mode}
            cancelButton={cancelButton}
            submitButton={submitButtonWithFlush}
            isUserAuthorizedForAddNote={isUserAuthorizedForAddNote}
            showAttachment={showAttachment}
            hideInsertImageButton={hideInsertImageButton}
          />
        )}
        {isReadOnlyCollapsible && shouldShowCollapseToggle && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
            <ButtonBase
              type="button"
              onClick={handleToggleCollapse}
              sx={{
                padding: 0,
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                  textUnderlineOffset: '3px',
                },
              }}
            >
              <Typography variant="body2">
                {isCollapsed ? 'View More' : 'View Less'}
              </Typography>
            </ButtonBase>
          </Box>
        )}
        {renderFilesView()}
      </Box>
    </MentionContext.Provider>
  );
};

TextEditor.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  userList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  transitionStates: PropTypes.arrayOf(PropTypes.string),
  threadForm: PropTypes.oneOfType([PropTypes.object]),
  entityId: PropTypes.number,
  readOnly: PropTypes.bool,
  disableTagAndTitleFields: PropTypes.bool,
  isEditorEmpty: PropTypes.bool,
  handleSubmit: PropTypes.func,
  mode: PropTypes.string,
  cancelButton: PropTypes.node,
  submitButton: PropTypes.node,
  isUserAuthorizedForAddNote: PropTypes.bool,
  onClose: PropTypes.func,
  compact: PropTypes.bool,
  showAttachment: PropTypes.bool,
  hideInsertImageButton: PropTypes.bool,
  files: PropTypes.oneOfType([PropTypes.object]),
  onAttachFile: PropTypes.func,
  onRemoveFile: PropTypes.func,
  placeholder: PropTypes.string,
  selectedEntityToInsert: PropTypes.oneOfType([PropTypes.object]),
  collapsible: PropTypes.bool,
  maxLines: PropTypes.number,
};

export default TextEditor;
